/**
 * Central navigation + permission gate configuration.
 *
 * Single source of truth for:
 *  1) Sidebar visibility (`<Sidebar>` consumes this list)
 *  2) Route-level guards (`<ProtectedRoute>` driven from the same entries
 *     so URL-typing cannot bypass sidebar gating)
 *
 * Visibility rules:
 *  - `alwaysVisible: true` → shown to every authenticated admin user, regardless
 *    of permission map. Used for the four base hubs Mustafa requires to surface
 *    in all cases: Attendance (`/work-time`), Rules (`/rules`), Billing
 *    (`/billing`), and System health (`/_health`).
 *  - `permissions: string[]` + `mode: 'any' | 'all'` → standard `usePermission`
 *    check. Hub leaves use `mode: 'any'` so a user holding only one of the
 *    hub's tabs still reaches the hub.
 *  - The admin-role bypass inside `usePermission` is preserved — `roles:
 *    ['admin']` users see every gated item even without explicit flags.
 */

import type { ComponentType } from 'react';
import {
  Activity,
  Award,
  BarChart3,
  Bell,
  CalendarRange,
  ClipboardList,
  CreditCard,
  FileText,
  GraduationCap,
  Hourglass,
  IdCard,
  LayoutDashboard,
  LayoutGrid,
  Megaphone,
  MessageSquareWarning,
  MonitorSmartphone,
  Settings,
  Sliders,
  UserPlus,
  Users,
} from 'lucide-react';

export type LucideIcon = ComponentType<{ className?: string }>;

/** Modern HR-system colour palette (mirrors `Sidebar.tsx`'s `TONE`). */
export const TONE = {
  indigo: '#6366f1',
  violet: '#8b5cf6',
  blue: '#3b82f6',
  sky: '#0ea5e9',
  cyan: '#06b6d4',
  teal: '#14b8a6',
  emerald: '#10b981',
  amber: '#f59e0b',
  rose: '#f43f5e',
  slate: '#64748b',
  red: '#ef4444',
  green: '#22c55e',
} as const;

export type Tone = (typeof TONE)[keyof typeof TONE];

export interface NavLeafConfig {
  /** Stable id used as React key + permission diagnostics. */
  id: string;
  /** Absolute route the leaf links to. */
  to: string;
  /** Translation key (with default Arabic fallback). */
  labelKey: string;
  /** Lucide icon component. */
  icon: LucideIcon;
  /** Tone hex value (one of `TONE.*`). */
  tone: Tone;
  /**
   * When true, the leaf bypasses every permission check. Used for the four
   * base hubs that must always surface (attendance, rules, billing, health).
   */
  alwaysVisible?: boolean;
  /**
   * Permission keys gating this leaf. Ignored when `alwaysVisible` is true.
   * Hub entries list every tab permission so the leaf shows when the user
   * holds any of them.
   */
  permissions?: string[];
  /** Match mode for `permissions`. Defaults to `'any'`. */
  mode?: 'any' | 'all';
}

/** Main sidebar entries — order here is the order rendered in the sidebar. */
export const NAV_ITEMS: NavLeafConfig[] = [
  {
    id: 'dashboard',
    to: '/dashboard',
    labelKey: 'nav.dashboard',
    icon: LayoutDashboard,
    tone: TONE.indigo,
    permissions: ['showDashboard'],
  },
  {
    id: 'staff',
    to: '/staff',
    labelKey: 'nav.staff',
    icon: Users,
    tone: TONE.violet,
    permissions: [
      'showStaffManangement',
      'showEmployees',
      'showUsers',
      'showDepartments',
      'showBranches',
      'showTagEmployee',
      'showEmployeeAttachments',
    ],
  },
  // Always visible — Attendance hub (إدارة الحضور).
  {
    id: 'workTime',
    to: '/work-time',
    labelKey: 'nav.workTime',
    icon: Hourglass,
    tone: TONE.amber,
    alwaysVisible: true,
  },
  {
    id: 'attendanceMgmt',
    to: '/attendance-management',
    labelKey: 'nav.attendanceMgmt',
    icon: CalendarRange,
    tone: TONE.emerald,
    permissions: [
      'showWorkManagement',
      'showLeaves',
      'showLeavesTypes',
      'showOfficialHolidays',
      'showRewardsPenalties',
      'showExtraMinutes',
      'showPartialTimeRequest',
    ],
  },
  // Employee Evaluation ("تقييم الموظفين") — people/HR category. Hub gate
  // mirrors the router: shown when the user holds any evaluation permission.
  {
    id: 'evaluation',
    to: '/evaluation',
    labelKey: 'nav.evaluation',
    icon: Award,
    tone: TONE.violet,
    permissions: ['evaluation.view', 'evaluation.manageTypes', 'evaluation.settings'],
  },
  // Employee surveys ("استبيانات الموظفين") — people/HR. Shown to anyone holding
  // any survey permission; the section itself gates each action.
  {
    id: 'surveys',
    to: '/surveys',
    labelKey: 'nav.surveys',
    icon: ClipboardList,
    tone: TONE.violet,
    permissions: ['surveys.view', 'surveys.create', 'surveys.edit'],
  },
  {
    id: 'devices',
    to: '/devices',
    labelKey: 'nav.devicesHub',
    icon: MonitorSmartphone,
    tone: TONE.sky,
    permissions: [
      'showDeviceManagement',
      'showDevices',
      'showRequests',
      'showUnregisteredEmployees',
      'showInDevicesEmployees',
      // Beacon management lives inside the Devices hub (tabs
      // `/devices/beacons` + `/devices/beacons/attempts`). A user holding only
      // `showBeacons` must still reach the hub to use those tabs.
      'showBeacons',
    ],
  },
  {
    id: 'students',
    to: '/students',
    labelKey: 'nav.studentsHub',
    icon: GraduationCap,
    tone: TONE.teal,
    permissions: [
      'showStudents',
      'showStages',
      'showSubjects',
      'showStudentAttendance',
      'showStudentDetails',
      'showNotificationsPending',
      'showAbsenceWarnings',
    ],
  },
  {
    id: 'reports',
    to: '/reports',
    labelKey: 'nav.reports',
    icon: BarChart3,
    tone: TONE.cyan,
    permissions: [
      'showReports',
      'showAttReport',
      'showAttLogDetails',
      'showMonthlyAttendanceReport',
      'showCapacityReport',
      'showEmpSummeryReport',
      'showSuddenlyCheckReport',
      'showSalarySlips',
      'showRealtimeAttend',
      'showReportLayouts',
    ],
  },
  // Always visible — Rules hub (القواعد).
  {
    id: 'rules',
    to: '/rules',
    labelKey: 'nav.rules',
    icon: Sliders,
    tone: TONE.blue,
    alwaysVisible: true,
  },
  {
    id: 'notifications',
    to: '/notifications',
    labelKey: 'nav.notifications',
    icon: Bell,
    tone: TONE.rose,
    permissions: ['showNotifications'],
  },
  {
    id: 'broadcast',
    to: '/notifications/broadcast',
    labelKey: 'nav.broadcast',
    icon: Megaphone,
    tone: TONE.violet,
    permissions: ['sendBroadcast'],
  },
  {
    id: 'notificationTemplates',
    to: '/notification-templates',
    labelKey: 'nav.notificationTemplates',
    icon: FileText,
    tone: TONE.sky,
    permissions: ['sendBroadcast'],
  },
  {
    id: 'recruitment',
    to: '/recruitment',
    labelKey: 'nav.recruitment',
    icon: UserPlus,
    tone: TONE.emerald,
    permissions: ['recruitment.view'],
  },
  {
    id: 'supervisorRequests',
    to: '/supervisor-requests',
    labelKey: 'nav.supervisorRequests',
    icon: ClipboardList,
    tone: TONE.amber,
    permissions: ['supervisorRequests.view'],
  },
  {
    id: 'complaints',
    to: '/complaints',
    labelKey: 'nav.complaints',
    icon: MessageSquareWarning,
    tone: TONE.rose,
    permissions: ['complaints.view'],
  },
  {
    id: 'visitors',
    to: '/visitors',
    labelKey: 'nav.visitors',
    icon: IdCard,
    tone: TONE.cyan,
    // `showVisitors` (flat, default-true) is what the backend `visitors.view`
    // gate maps to — mirror it so default (non-admin) users still see the module.
    permissions: ['showVisitors'],
  },
  {
    id: 'settings',
    to: '/settings',
    labelKey: 'nav.settings',
    icon: Settings,
    tone: TONE.slate,
    permissions: ['showPublicSettings', 'showAuditLog', 'showEmployees'],
  },
  // Quick Access launcher — placed LAST in the sidebar. Visible to every user
  // (each sees only the screens assigned to them; the builder underneath is
  // gated by `showQuickAccess`).
  {
    id: 'quickAccess',
    to: '/quick-access',
    labelKey: 'nav.quickAccess',
    icon: LayoutGrid,
    tone: TONE.violet,
    alwaysVisible: true,
  },
];

/** Footer entries — rendered below the main list. */
export const FOOTER_ITEMS: NavLeafConfig[] = [
  // Always visible — Billing (الفواتير).
  {
    id: 'billing',
    to: '/billing',
    labelKey: 'nav.billing',
    icon: CreditCard,
    tone: TONE.red,
    alwaysVisible: true,
  },
  // Always visible — System health (حالة النظام).
  {
    id: 'health',
    to: '/_health',
    labelKey: 'nav.health',
    icon: Activity,
    tone: TONE.green,
    alwaysVisible: true,
  },
];

/**
 * Build the props for `<ProtectedRoute>` from a leaf id, so route guards stay
 * in sync with sidebar gating. Returns `undefined` when the leaf is always
 * visible — callers should mount the route without a permission prop.
 */
export function getRouteGuardProps(
  id: string,
): { permission?: string | string[]; alwaysVisible?: true } {
  const leaf =
    NAV_ITEMS.find((it) => it.id === id) ??
    FOOTER_ITEMS.find((it) => it.id === id);
  if (!leaf) return {};
  if (leaf.alwaysVisible) return { alwaysVisible: true };
  if (!leaf.permissions || leaf.permissions.length === 0) return {};
  return {
    permission: leaf.permissions.length === 1 ? leaf.permissions[0]! : leaf.permissions,
  };
}
